"use client";

import Link from "next/link";
import { siteConfig } from "@/config/site";
import { navLinks } from "@/config/navigation";
import { useTranslation } from "@/i18n";
import { SteamLogo } from "@/components/ui/SteamLogo";

export function Footer() {
    const { t } = useTranslation();
    return (
        <footer className="relative border-t border-border bg-void">
            <div className="mx-auto max-w-6xl px-6 md:px-8 py-12 md:py-16">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-16">
                    {/* Brand */}
                    <div className="space-y-4">
                        <p className="text-sm font-bold tracking-wider text-text">
                            {siteConfig.studioName.toUpperCase()}
                        </p>
                        <p className="text-sm text-text-muted leading-relaxed max-w-xs">
                            {t.footer.closingLine}
                        </p>
                    </div>

                    {/* Navigation */}
                    <div className="space-y-4">
                        <p className="text-xs font-semibold uppercase tracking-[0.15em] text-text-secondary">
                            {t.footer.navigation}
                        </p>
                        <div className="flex flex-col gap-2">
                            {navLinks.map((link) => (
                                <a
                                    key={link.href}
                                    href={link.href}
                                    className="text-sm text-text-muted hover:text-text transition-colors w-fit"
                                >
                                    {t.nav[link.label.toLowerCase() as keyof typeof t.nav] ?? link.label}
                                </a>
                            ))}
                        </div>
                    </div>

                    {/* Social / Connect */}
                    <div className="space-y-4">
                        <p className="text-xs font-semibold uppercase tracking-[0.15em] text-text-secondary">
                            {t.footer.connect}
                        </p>
                        <div className="flex flex-col gap-4">
                            {siteConfig.steamUrl && (
                                <a
                                    href={siteConfig.steamUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-text-muted hover:text-mars transition-colors w-fit"
                                    aria-label="Steam"
                                >
                                    <SteamLogo
                                        width={72}
                                        height={18}
                                        className="h-[18px] w-auto opacity-80 transition-opacity hover:opacity-100"
                                    />
                                </a>
                            )}
                            <div className="flex items-center gap-4">
                                {siteConfig.social.instagram && (
                                    <a
                                        href={siteConfig.social.instagram}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-text-muted hover:text-mars transition-colors"
                                        aria-label="Instagram"
                                    >
                                        <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5" aria-hidden="true">
                                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" />
                                        </svg>
                                    </a>
                                )}
                                {siteConfig.social.tiktok && (
                                    <a
                                        href={siteConfig.social.tiktok}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-text-muted hover:text-mars transition-colors"
                                        aria-label="TikTok"
                                    >
                                        <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5" aria-hidden="true">
                                            <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.75a4.85 4.85 0 0 1-1.01-.06z" />
                                        </svg>
                                    </a>
                                )}
                                {siteConfig.social.youtube && (
                                    <a
                                        href={siteConfig.social.youtube}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-text-muted hover:text-mars transition-colors"
                                        aria-label="YouTube"
                                    >
                                        <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5" aria-hidden="true">
                                            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                                        </svg>
                                    </a>
                                )}
                                {siteConfig.social.twitter && (
                                    <a
                                        href={siteConfig.social.twitter}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-text-muted hover:text-mars transition-colors"
                                        aria-label="Twitter / X"
                                    >
                                        <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5" aria-hidden="true">
                                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                                        </svg>
                                    </a>
                                )}
                                {siteConfig.social.discord && (
                                    <a
                                        href={siteConfig.social.discord}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-text-muted hover:text-mars transition-colors"
                                        aria-label="Discord"
                                    >
                                        <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5" aria-hidden="true">
                                            <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.095 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.095 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
                                        </svg>
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Legal */}
                    <div className="space-y-4">
                        <p className="text-xs font-semibold uppercase tracking-[0.15em] text-text-secondary">
                            {t.footer.legal}
                        </p>
                        <div className="flex flex-col gap-2">
                            <Link
                                href="/legal#legal-notice"
                                className="text-sm text-text-muted hover:text-text transition-colors w-fit"
                            >
                                {t.footer.legalNotice}
                            </Link>
                            <Link
                                href="/legal#privacy"
                                className="text-sm text-text-muted hover:text-text transition-colors w-fit"
                            >
                                {t.footer.privacyPolicy}
                            </Link>
                            <Link
                                href="/legal#cookies"
                                className="text-sm text-text-muted hover:text-text transition-colors w-fit"
                            >
                                {t.footer.cookies}
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Bottom bar */}
                <div className="mt-12 pt-8 border-t border-border">
                    <p className="text-xs text-text-muted text-center">
                        {t.footer.copyright}
                    </p>
                </div>
            </div>
        </footer>
    );
}
